/**
 * AgenteLead Search OS — Company Dossier (Phase 2 + 3).
 *
 * Full intelligence page for a discovered company:
 *   • Overview + Opportunity + Confidence
 *   • Deep Research (one-click) — runs enrich + DMs + signals + vendor
 *   • Decision Makers grid
 *   • Buying Signals timeline
 *   • Current Vendor investigation
 *   • Evidence stream (all sources retained)
 *   • Research history
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Users, Zap, ShieldAlert, FileSearch, Loader2,
  ArrowLeft, Sparkles, RefreshCw, ExternalLink, Mail, Linkedin,
  MapPin, Globe, ChevronRight, AlertTriangle, Clock,
  TrendingUp, Send, ShieldCheck,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import { useClaireContext } from "../../hooks/useClaireContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const TIER_COLORS = {
  "A+": "linear-gradient(135deg,#10B981 0%,#065F46 100%)",
  "A":  "linear-gradient(135deg,#10B981 0%,#047857 100%)",
  "B":  "linear-gradient(135deg,#3B82F6 0%,#1E3A8A 100%)",
  "C":  "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)",
  "D":  "linear-gradient(135deg,#64748B 0%,#334155 100%)",
};

const CONFIDENCE_COLORS = {
  VERY_HIGH: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  HIGH:      "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
  MEDIUM:    "text-sky-300 border-sky-500/30 bg-sky-500/5",
  LOW:       "text-amber-300 border-amber-500/30 bg-amber-500/5",
  VERY_LOW:  "text-rose-300 border-rose-500/40 bg-rose-500/10",
};

const VENDOR_STATUS_COLORS = {
  CONFIRMED:    "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  PROBABLE:     "bg-sky-500/20 text-sky-300 border-sky-500/40",
  POSSIBLE:     "bg-amber-500/20 text-amber-300 border-amber-500/40",
  CONFLICTING:  "bg-rose-500/20 text-rose-300 border-rose-500/40",
  NOT_FOUND:    "bg-slate-500/20 text-slate-300 border-slate-500/40",
};

const SIGNAL_TYPE_LABELS = {
  new_facility:      "🏢 Nova instalação",
  expansion:         "📈 Expansão",
  new_contract:      "📝 Novo contrato",
  rfp:               "📢 RFP",
  hiring_security:   "👮 Contratando segurança",
  hiring_facilities: "🛠 Contratando facilities",
  acquisition:       "🤝 Aquisição",
  leadership_change: "🎯 Mudança de liderança",
  incident:          "⚠️ Incidente",
  renovation:        "🏗 Reforma",
  other:             "• Outro",
};

export default function SmithAISearchCompany() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [conflictReport, setConflictReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // "enrich" | "dm" | "signals" | "vendor" | "deep"

  const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ai-search/companies/${id}/dossier`, authHeader);
      setDossier(r.data);
      // Iter326 · Fetch cross-entity contamination report in parallel.
      // If the endpoint 404s (company not yet indexed by conflict route),
      // silently skip — we don't want to block the dossier render.
      axios.get(`${API}/smith/ai-search/companies/${id}/conflict-report`, authHeader)
        .then((cr) => setConflictReport(cr.data))
        .catch(() => setConflictReport(null));
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao carregar dossiê");
      if (ex?.response?.status === 404) navigate("/smith/ai-search");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Iter326 · Publish this dossier as Claire's context (must run
  // unconditionally to respect Rules of Hooks — reads optional chain
  // so dossier=null before load resolves is fine).
  useClaireContext({
    page: "ai-search-company-detail",
    selected_entity: {
      id,
      name: dossier?.company?.name,
      kind: "company",
      domain: dossier?.company?.website || dossier?.company?.domain,
    },
    filters: {},
    visible_count: dossier ? 1 : 0,
    project: "Smith Protective",
    entity_conflicts: conflictReport?.conflicts?.length || 0,
    contamination_rate: conflictReport?.contamination_rate || 0,
  });

  const runAction = async (kind, endpoint, label) => {
    setBusy(kind);
    try {
      const r = await axios.post(`${API}/ai-search/companies/${id}/${endpoint}`, {},
        { ...authHeader, timeout: 200000 });
      if (r.data?.ok === false) {
        toast.error(r.data?.error || `${label} falhou`);
      } else {
        toast.success(`${label} concluído`);
        load();
      }
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || `${label} falhou`);
    } finally { setBusy(null); }
  };

  const runDeep = async () => {
    // Iter205 — pre-flight cost estimate + confirm gate.
    try {
      const est = await axios.get(
        `${API}/ai-search/companies/${id}/deep-research/estimate`,
        authHeader);
      const e = est.data || {};
      if (e.requires_confirm) {
        const msg =
          `Deep Research estimado ~$${e.estimate_usd} USD ` +
          `(pior caso $${e.high_estimate_usd}). ` +
          `Cap configurado: $${e.deep_research_cap_usd}. Prosseguir?`;
        // eslint-disable-next-line no-alert
        if (!window.confirm(msg)) {
          toast.info("Deep Research cancelado");
          return;
        }
      }
    } catch { /* if estimate fails, still allow the run */ }
    setBusy("deep");
    try {
      await axios.post(`${API}/ai-search/companies/${id}/deep-research`, {},
        { ...authHeader, timeout: 610000 });
      toast.success("Deep Research concluído — dossiê atualizado");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Deep Research falhou");
    } finally { setBusy(null); }
  };

  const goDraftOutreach = () => {
    navigate(`/smith/outreach?tab=compose&ai_company_id=${id}`);
  };

  if (loading || !dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (loading || !dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: NAVY }}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  const c = dossier.company || {};
  const counts = dossier.counts || {};
  const hq = c.headquarters || {};
  const tierBg = TIER_COLORS[c.opportunity_tier] || TIER_COLORS.D;
  const confCls = CONFIDENCE_COLORS[c.confidence_band] || CONFIDENCE_COLORS.LOW;

  return (
    <div className="min-h-screen" style={{ background: NAVY, color: "#E2E8F0" }}>
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-ai-company-header"
          title={c.name || "Company"}
          subtitle={`AI Search Dossier · ${c.industry || "sem setor"} · ${hq.city || ""} ${hq.state || ""}`}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: tierBg, color: "white" }}>
              <Building2 className="w-5 h-5" />
            </div>
          }
        >
          <button type="button" onClick={() => navigate("/smith/ai-search")}
            data-testid="ai-company-back-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold text-white hover:border-purple-500/60 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> AI Lead Search
          </button>
        </AppHeader>

        <main className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-6">
          {/* Iter326 · Cross-entity contamination banner. Only renders
              when the conflict-report endpoint flags >= 20% of evidence
              rows pointing at a different domain. Read-only warning —
              no auto-merge, no auto-delete. */}
          {conflictReport?.warning === "cross_entity_contamination_detected" && (
            <section
              data-testid="ai-company-contamination-banner"
              className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 flex items-start gap-3">
              <div className="text-amber-300 text-xl leading-none">⚠</div>
              <div className="text-[13px] text-amber-100 space-y-1">
                <div className="font-bold uppercase tracking-wide text-[11.5px] text-amber-300">
                  Possível mistura de empresas neste dossiê
                </div>
                <div>
                  Detectamos <span className="font-bold">
                    {conflictReport.conflicts?.length}
                  </span> evidência(s) de <span className="font-bold">
                    {conflictReport.total_evidence}
                  </span> apontando para domínios diferentes do declarado
                  ({conflictReport.company_domain || "sem domínio"}).
                  Taxa de contaminação: <span className="font-bold">
                    {conflictReport.contamination_rate}%
                  </span>.
                </div>
                <details className="mt-1">
                  <summary className="cursor-pointer text-[11.5px] hover:underline">
                    Ver hosts em conflito ({(conflictReport.conflicts || []).length})
                  </summary>
                  <ul className="mt-1 space-y-0.5 text-[11.5px] font-mono opacity-80">
                    {(conflictReport.conflicts || []).slice(0, 8).map((cf, i) => (
                      <li key={i}>• {cf.host}</li>
                    ))}
                  </ul>
                </details>
                <div className="text-[11.5px] opacity-80">
                  Revise antes de agir. Nenhuma evidência foi removida
                  automaticamente.
                </div>
              </div>
            </section>
          )}

          {/* Hero */}
          <section data-testid="ai-company-hero"
            className="rounded-2xl border border-white/10 p-5"
            style={{ background: "linear-gradient(135deg,rgba(124,58,237,0.08) 0%,rgba(15,23,42,0.4) 100%)" }}>
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px] space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10.5px] uppercase tracking-widest px-2 py-1 rounded font-bold text-white"
                        style={{ background: tierBg }}
                        data-testid="ai-company-tier">
                    {c.opportunity_tier} · {c.opportunity_score}
                  </span>
                  <span className={`text-[10.5px] uppercase tracking-widest px-2 py-1 rounded border font-bold ${confCls}`}
                        data-testid="ai-company-confidence">
                    {c.confidence_band?.replace("_", " ")} · {c.confidence_overall}
                  </span>
                  {c.website && (
                    <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                       target="_blank" rel="noreferrer"
                       className="text-[11px] px-2 py-1 rounded border border-white/15 text-slate-300 hover:border-purple-500/50 hover:text-white inline-flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {c.website}
                    </a>
                  )}
                  {(hq.city || hq.state) && (
                    <span className="text-[11px] px-2 py-1 text-slate-400 inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {hq.city}, {hq.state}
                    </span>
                  )}
                </div>
                {c.description && <p className="text-[13px] text-slate-300 leading-relaxed">{c.description}</p>}

                {/* Iter246 · Cross-provider consensus chip */}
                <ConsensusChip consensus={c.consensus}
                                     gemini={c.gemini_second_opinion}
                                     perplexity={c.perplexity_audit} />

                {c.opportunity_reasons?.length > 0 && (
                  <div className="pt-2">
                    <div className="text-[10.5px] uppercase tracking-widest text-emerald-300 font-bold mb-1.5">
                      Por que essa empresa?
                    </div>
                    <ul className="space-y-1">
                      {c.opportunity_reasons.map((r, i) => (
                        <li key={i} className="text-[12.5px] text-slate-200 flex items-start gap-1.5">
                          <ChevronRight className="w-3 h-3 mt-0.5 text-emerald-400 shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex flex-col gap-2 w-full lg:w-auto min-w-[220px]">
                <button type="button" onClick={runDeep} disabled={busy !== null}
                  data-testid="ai-company-deep-research-btn"
                  className="h-10 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4C1D95 100%)" }}>
                  {busy === "deep" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {busy === "deep" ? "Pesquisando (60-180s)..." : "🔎 Deep Research (tudo)"}
                </button>
                <button type="button" onClick={() => runAction("dm", "decision-makers", "Decision Makers")} disabled={busy !== null}
                  data-testid="ai-company-find-dm-btn"
                  className="h-9 px-3 rounded-lg border border-white/20 text-[11.5px] font-bold text-white hover:border-purple-500/60 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {busy === "dm" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                  👤 Find Decision Makers
                </button>
                <button type="button" onClick={() => runAction("signals", "buying-signals", "Buying Signals")} disabled={busy !== null}
                  data-testid="ai-company-find-signals-btn"
                  className="h-9 px-3 rounded-lg border border-white/20 text-[11.5px] font-bold text-white hover:border-orange-500/60 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {busy === "signals" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  🔥 Find Buying Signals
                </button>
                <button type="button" onClick={() => runAction("vendor", "vendor", "Current Vendor")} disabled={busy !== null}
                  data-testid="ai-company-find-vendor-btn"
                  className="h-9 px-3 rounded-lg border border-white/20 text-[11.5px] font-bold text-white hover:border-red-500/60 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {busy === "vendor" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                  🛡 Find Current Vendor
                </button>
                <button type="button" onClick={() => runAction("enrich", "enrich", "Enrich")} disabled={busy !== null}
                  data-testid="ai-company-enrich-btn"
                  className="h-9 px-3 rounded-lg border border-white/20 text-[11.5px] font-bold text-white hover:border-emerald-500/60 inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                  {busy === "enrich" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  ✨ Enrich with AI
                </button>
                <button type="button" onClick={goDraftOutreach}
                  data-testid="ai-company-draft-outreach-btn"
                  className="h-9 px-3 rounded-lg text-[11.5px] font-bold text-white inline-flex items-center justify-center gap-1.5"
                  style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                  <Send className="w-3.5 h-3.5" /> Draft Outreach
                </button>
              </div>
            </div>

            {/* Counts */}
            <div className="grid grid-cols-4 gap-2 pt-4 mt-4 border-t border-white/5">
              <CountTile icon={FileSearch} label="Evidências" v={counts.evidence || 0} />
              <CountTile icon={Users} label="Contatos" v={counts.contacts || 0} />
              <CountTile icon={Zap} label="Signals" v={counts.signals || 0} />
              <CountTile icon={ShieldAlert} label="Vendors" v={counts.vendors || 0} />
            </div>
          </section>

          {/* Decision Makers */}
          <Section title="👤 Decision Makers" testid="ai-company-dm-section"
            count={dossier.contacts?.length}
            emptyLabel="Nenhum decision maker mapeado. Rode 'Find Decision Makers' para começar.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(dossier.contacts || []).map((ct) => (
                <ContactCard key={ct.id} c={ct} />
              ))}
            </div>
          </Section>

          {/* Buying Signals timeline */}
          <Section title="🔥 Buying Signals" testid="ai-company-signals-section"
            count={dossier.signals?.length}
            emptyLabel="Nenhum sinal de compra detectado. Rode 'Find Buying Signals' para investigar gatilhos recentes.">
            <ol className="space-y-2 border-l-2 border-orange-500/20 pl-4">
              {(dossier.signals || []).map((s) => (
                <SignalItem key={s.id} s={s} />
              ))}
            </ol>
          </Section>

          {/* Current Vendor */}
          <Section title="🛡 Current Vendor" testid="ai-company-vendor-section"
            count={dossier.vendors?.length}
            emptyLabel="Vendor atual não investigado. Rode 'Find Current Vendor'.">
            {c.vendor_displacement_note && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12.5px] text-emerald-100 mb-3"
                   data-testid="ai-company-displacement-note">
                <div className="text-[10.5px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
                  Displacement opportunity
                </div>
                {c.vendor_displacement_note}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(dossier.vendors || []).map((v) => (
                <VendorCard key={v.id} v={v} />
              ))}
            </div>
          </Section>

          {/* Evidence stream */}
          <Section title="📄 Evidências" testid="ai-company-evidence-section"
            count={dossier.evidence?.length}
            emptyLabel="Sem evidências ainda.">
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {(dossier.evidence || []).map((ev) => (
                <EvidenceRow key={ev.id || (ev.source_url + ev.field_name)} ev={ev} />
              ))}
            </div>
          </Section>

          {/* Research history */}
          {dossier.runs?.length > 0 && (
            <Section title="⏱ Histórico de Pesquisas" testid="ai-company-history-section"
              count={dossier.runs.length}
              emptyLabel="Nenhum run registrado.">
              <div className="rounded-lg border border-white/10 divide-y divide-white/5">
                {dossier.runs.map((r) => (
                  <div key={r.id} className="p-3 flex items-center gap-3 text-[11.5px]">
                    <div className={`h-6 px-2 rounded text-[10px] font-bold inline-flex items-center ${
                      r.status === "COMPLETED" ? "bg-emerald-500/15 text-emerald-300"
                        : r.status === "FAILED" ? "bg-rose-500/15 text-rose-300"
                        : "bg-slate-500/15 text-slate-300"}`}>
                      {r.status}
                    </div>
                    <div className="text-slate-400 font-mono text-[10px]">{(r.id || "").slice(0,8)}</div>
                    <div className="text-slate-500 flex-1">{r.model_used}</div>
                    <div className="text-slate-500">${r.budget_snapshot?.cost_used_usd || "0.000000"}</div>
                    <div className="text-slate-500">{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </main>
      </div>
    </div>
  );
}

// ── Sub components ──────────────────────────────────────────────────────

function Section({ title, testid, count, emptyLabel, children }) {
  const empty = count === 0 || count === undefined;
  return (
    <section data-testid={testid} className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] uppercase tracking-widest font-bold text-slate-200">
          {title} {count !== undefined && <span className="text-slate-500 ml-1">({count})</span>}
        </h2>
      </div>
      {empty ? (
        <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-[11.5px] text-slate-500">
          {emptyLabel}
        </div>
      ) : children}
    </section>
  );
}

function CountTile({ icon: Icon, label, v }) {
  return (
    <div className="rounded-lg border border-white/10 p-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <div>
        <div className="text-[9.5px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-[14px] font-bold text-white tabular-nums">{v}</div>
      </div>
    </div>
  );
}

function ContactCard({ c }) {
  const seniorityLabel = {
    c_level: "C-Level", vp: "VP", director: "Director",
    manager: "Manager", individual: "IC", unknown: "?",
  }[c.seniority] || c.seniority;
  const emailBadge = {
    VERIFIED_PUBLIC: { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "verificado" },
    INFERRED:        { cls: "bg-amber-500/20 text-amber-200 border-amber-500/40", label: "⚠ inferido" },
    NOT_FOUND:       { cls: "bg-slate-500/15 text-slate-400 border-slate-500/30", label: "sem email" },
  }[c.email_status] || { cls: "bg-slate-500/10 text-slate-400 border-slate-500/20", label: "—" };
  // Defence-in-depth: only render real http(s) URLs as clickable links so
  // stray citation tokens like "turn0search6" never leak into the UI.
  const realUrls = (c.source_urls || []).filter((u) => typeof u === "string" && /^https?:\/\//i.test(u));
  const profileUrl = c.public_profile_url && /^https?:\/\//i.test(c.public_profile_url) ? c.public_profile_url : null;
  return (
    <div data-testid={`ai-company-contact-${c.id}`}
      className="rounded-lg border border-white/10 p-3 space-y-2">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-white truncate">{c.full_name}</div>
          <div className="text-[11px] text-slate-400 truncate">{c.job_title}</div>
          <div className="text-[10px] text-slate-500 truncate">
            {c.department} {seniorityLabel && `· ${seniorityLabel}`}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10.5px] font-bold text-purple-300">DM {c.decision_maker_score}</div>
          <div className="text-[9px] uppercase tracking-widest text-slate-500">score</div>
        </div>
      </header>
      <div className="flex flex-wrap gap-1.5">
        {c.public_email && (
          <a href={`mailto:${c.public_email}`}
             className="text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-slate-200 hover:border-purple-500/50 inline-flex items-center gap-1 truncate max-w-full">
            <Mail className="w-2.5 h-2.5" /> {c.public_email}
          </a>
        )}
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${emailBadge.cls}`}>
          {emailBadge.label}
        </span>
        {profileUrl && (
          <a href={profileUrl} target="_blank" rel="noreferrer"
             className="text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-slate-300 hover:border-sky-500/50 inline-flex items-center gap-1">
            <Linkedin className="w-2.5 h-2.5" /> perfil
          </a>
        )}
      </div>
      {c.evidence_notes && <div className="text-[11px] italic text-slate-400">"{c.evidence_notes}"</div>}
      {realUrls.length > 0 && (
        <div className="pt-1 border-t border-white/5">
          {realUrls.slice(0, 2).map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer"
               className="block text-[10px] text-sky-400 hover:text-sky-300 truncate">
              <ExternalLink className="w-2.5 h-2.5 inline mr-1" /> {u}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function SignalItem({ s }) {
  const label = SIGNAL_TYPE_LABELS[s.signal_type] || s.signal_type;
  const relevanceCls = s.commercial_relevance_score >= 80 ? "text-emerald-300"
    : s.commercial_relevance_score >= 60 ? "text-sky-300"
    : "text-slate-400";
  return (
    <li data-testid={`ai-company-signal-${s.id}`}
      className="relative pl-4 -ml-2">
      <span className="absolute left-[-9px] top-2 w-3 h-3 rounded-full border-2 border-orange-500 bg-slate-900" />
      <div className="rounded-lg border border-white/10 p-3 space-y-1.5 bg-black/20">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-bold text-orange-300">{label}</div>
          <span className={`text-[10px] font-bold ${relevanceCls}`}>
            <TrendingUp className="w-2.5 h-2.5 inline" /> {s.commercial_relevance_score}
          </span>
        </div>
        <div className="text-[12.5px] text-white font-medium">{s.title}</div>
        {s.description && <p className="text-[11px] text-slate-300">{s.description}</p>}
        <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
          {s.signal_date && <span><Clock className="w-2.5 h-2.5 inline mr-1" /> {s.signal_date}</span>}
          {s.location && <span>• {s.location}</span>}
        </div>
        <a href={s.source_url} target="_blank" rel="noreferrer"
           className="text-[10.5px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 break-all">
          <ExternalLink className="w-2.5 h-2.5" /> {s.source_title || s.source_url}
        </a>
      </div>
    </li>
  );
}

function VendorCard({ v }) {
  const statusCls = VENDOR_STATUS_COLORS[v.status] || VENDOR_STATUS_COLORS.NOT_FOUND;
  const realUrls = (v.source_urls || []).filter((u) => typeof u === "string" && /^https?:\/\//i.test(u));
  return (
    <div data-testid={`ai-company-vendor-${v.id}`}
      className="rounded-lg border border-white/10 p-3 space-y-2">
      <header className="flex items-center justify-between gap-2">
        <div className="text-[13px] font-bold text-white truncate">{v.vendor_name}</div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${statusCls}`}>
          {v.status}
        </span>
      </header>
      <div className="text-[10.5px] text-slate-400">
        {v.vendor_type?.replace("_", " ")} · confidence {v.confidence_score}
        {v.contract_end_date && ` · vence ${v.contract_end_date}`}
      </div>
      {v.notes && <p className="text-[11px] text-slate-300">{v.notes}</p>}
      {realUrls.length > 0 && (
        <div className="pt-1 border-t border-white/5 space-y-0.5">
          {realUrls.slice(0, 3).map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noreferrer"
               className="block text-[10px] text-sky-400 hover:text-sky-300 truncate">
              <ExternalLink className="w-2.5 h-2.5 inline mr-1" /> {u}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function EvidenceRow({ ev }) {
  return (
    <div className="rounded border border-white/5 p-2.5 space-y-1 bg-black/10">
      <div className="flex items-center gap-2 text-[10px]">
        <span className="uppercase tracking-widest text-purple-300 font-bold">{ev.field_name}</span>
        <span className="text-slate-500">·</span>
        <span className="text-slate-400">{ev.source_type}</span>
      </div>
      <div className="text-[12px] text-white break-words">{ev.claimed_value || "—"}</div>
      {ev.excerpt && <div className="text-[11px] italic text-slate-400 line-clamp-2">"{ev.excerpt}"</div>}
      <a href={ev.source_url} target="_blank" rel="noreferrer"
         className="text-[10px] text-sky-400 hover:text-sky-300 inline-flex items-center gap-1 break-all">
        <ExternalLink className="w-2.5 h-2.5" /> {ev.source_title || ev.source_url}
      </a>
    </div>
  );
}


/**
 * Iter246 · Provider Consensus Chip.
 *
 * Reads the persisted `consensus` field from the company doc (populated
 * by `POST /api/ai-search/companies/{id}/deep-research`). Shows a
 * compact status pill so leadership can eyeball verdict alignment
 * without opening the raw audit blocks.
 */
function ConsensusChip({ consensus, gemini, perplexity }) {
  if (!consensus) return null;
  const agree = [];
  const disagree = [];
  const unavailable = [];
  const labels = { primary: "OpenAI", gemini: "Gemini", perplexity: "Perplexity" };
  Object.entries(labels).forEach(([k, label]) => {
    const s = consensus[k];
    if (s === "agree") agree.push(label);
    else if (s === "disagree") disagree.push(label);
    else unavailable.push(label);
  });
  const totalActive = agree.length + disagree.length;
  const consensusLabel =
    disagree.length === 0
      ? `${agree.length}/${totalActive} providers agree`
      : `${agree.join(" + ")} disagree${disagree.length ? " with " + disagree.join(" + ") : ""}`;
  const tone = disagree.length === 0
    ? { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-200", dot: "#10B981" }
    : { bg: "bg-amber-500/15",    border: "border-amber-400/40",   text: "text-amber-200",    dot: "#F59E0B" };
  const title = [
    gemini?.verdict && `Gemini: ${gemini.verdict}`,
    perplexity?.corroborating_urls?.length && `Perplexity found ${perplexity.corroborating_urls.length} corroborating URLs`,
  ].filter(Boolean).join(" · ") || "Cross-provider audit result";
  return (
    <div className="flex items-center gap-2 flex-wrap"
         data-testid="ai-company-consensus-chip">
      <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border ${tone.bg} ${tone.border} ${tone.text}`}
            title={title}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
        {consensusLabel}
      </span>
      {consensus.gemini_confidence != null && (
        <span className="text-[10px] text-slate-400 tabular-nums">
          Gemini conf. {consensus.gemini_confidence}%
        </span>
      )}
      {consensus.perplexity_confidence != null && (
        <span className="text-[10px] text-slate-400 tabular-nums">
          Perplexity conf. {consensus.perplexity_confidence}%
        </span>
      )}
      {unavailable.length > 0 && (
        <span className="text-[10px] text-slate-500"
              data-testid="ai-company-consensus-unavailable">
          {unavailable.join(" + ")} indisponível
        </span>
      )}
    </div>
  );
}
